import axios from 'axios';
import { ENDPOINTS } from '../constants/endpoints';
import { AuthService } from './auth.service';
import { Account } from '../interfaces/accounting.interface';

export const AccountService = {
    getAccountsByInstitution: async (page: number = 0, size: number = 10, includeHidden: boolean = false) => {
        const institutionId = AuthService.getSelectedInstitution();
        const response = await axios.get(`${ENDPOINTS.ACCOUNT.LIST_BY_INSTITUTION(institutionId!)}?page=${page}&size=${size}&includeHidden=${includeHidden}`);
        return response.data;
    },
    // Loads the ENTIRE chart of accounts (all pages), not just the first page.
    // Account dropdowns must show every account; a single fixed page size silently
    // dropped leaf accounts once the chart grew past the page limit.
    getAllAccounts: async (includeHidden: boolean = false): Promise<Account[]> => {
        const pageSize = 500;
        const first = await AccountService.getAccountsByInstitution(0, pageSize, includeHidden);
        if (Array.isArray(first)) return first;
        const all: Account[] = [...(first.content ?? [])];
        const totalPages: number = first.totalPages ?? 1;
        for (let page = 1; page < totalPages; page++) {
            const next = await AccountService.getAccountsByInstitution(page, pageSize, includeHidden);
            all.push(...(Array.isArray(next) ? next : (next.content ?? [])));
        }
        return all;
    },
    getAccountsTree: async () => {
        const institutionId = AuthService.getSelectedInstitution();
        const response = await axios.get(ENDPOINTS.ACCOUNT.TREE_BY_INSTITUTION(institutionId!));
        return response.data;
    },
    getAccountsByType: async (type: string) => {
        const institutionId = AuthService.getSelectedInstitution();
        const response = await axios.get(ENDPOINTS.ACCOUNT.BY_TYPE(institutionId!, type));
        return response.data;
    },
    getAccountById: async (id: string) => {
        const response = await axios.get(ENDPOINTS.ACCOUNT.BY_ID(id));
        return response.data;
    },
    createAccount: async (account: Account) => {
        const response = await axios.post(ENDPOINTS.ACCOUNT.BASE, account);
        return response.data;
    },
    updateAccount: async (id: string, account: Account) => {
        const response = await axios.put(ENDPOINTS.ACCOUNT.UPDATE(id), account);
        return response.data;
    },
    deleteAccount: async (id: string) => {
        await axios.delete(ENDPOINTS.ACCOUNT.DELETE(id));
    },
    hideAccount: async (accountId: string) => {
        const institutionId = AuthService.getSelectedInstitution();
        await axios.post(ENDPOINTS.ACCOUNT.HIDE(institutionId!, accountId));
    },
    unhideAccount: async (accountId: string) => {
        const institutionId = AuthService.getSelectedInstitution();
        await axios.delete(ENDPOINTS.ACCOUNT.UNHIDE(institutionId!, accountId));
    },
    getHiddenAccounts: async (): Promise<string[]> => {
        const institutionId = AuthService.getSelectedInstitution();
        const response = await axios.get(ENDPOINTS.ACCOUNT.HIDDEN(institutionId!));
        return response.data;
    },
    getAccountLedger: async (accountId: string, from: string, to: string) => {
        const institutionId = AuthService.getSelectedInstitution();
        const response = await axios.get(ENDPOINTS.ACCOUNT.LEDGER(accountId), {
            params: { institutionId, from, to },
        });
        return response.data;
    },
    getTrialBalance: async (from: string, to: string) => {
        const institutionId = AuthService.getSelectedInstitution();
        const response = await axios.get(ENDPOINTS.ACCOUNT.TRIAL_BALANCE(institutionId!), {
            params: { from, to },
        });
        return response.data;
    },
    getSegmentTrialBalance: async (from: string, to: string, segmentId?: string) => {
        const institutionId = AuthService.getSelectedInstitution();
        const response = await axios.get(ENDPOINTS.ACCOUNT.SEGMENT_TRIAL_BALANCE(institutionId!), { params: { from, to, ...(segmentId ? { segmentId } : {}) } });
        return response.data;
    },
    getIncomeStatement: async (from: string, to: string, segmentId?: string) => {
        const institutionId = AuthService.getSelectedInstitution();
        const response = await axios.get(ENDPOINTS.ACCOUNT.INCOME_STATEMENT(institutionId!), { params: { from, to, ...(segmentId ? { segmentId } : {}) } });
        return response.data;
    },
    getBalanceSheet: async (asOf: string, segmentId?: string) => {
        const institutionId = AuthService.getSelectedInstitution();
        const response = await axios.get(ENDPOINTS.ACCOUNT.BALANCE_SHEET(institutionId!), { params: { asOf, ...(segmentId ? { segmentId } : {}) } });
        return response.data;
    },
};
